// Small reusable primitives: Btn / IconBtn / Avatar / Pill / Tag / Chip /
// Score / ScoreGlyph / Toggle / Tabs / TextInput / TextArea. All typed.

import React from "react";
import { Ic } from "./Icon";
import type { Status, TeamMember } from "../types";
import { findMember, useAppData } from "../store";

type BtnKind = "default" | "primary" | "accent" | "ghost";

interface BtnProps {
  children?: React.ReactNode;
  kind?: BtnKind;
  size?: "sm";
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  ico?: string;
  type?: "button" | "submit";
  disabled?: boolean;
  style?: React.CSSProperties;
  title?: string;
}

export function Btn({
  children, kind = "default", size, onClick, ico, type = "button",
  disabled, style, title,
}: BtnProps) {
  const cls = ["btn"];
  if (kind === "primary") cls.push("primary");
  if (kind === "accent")  cls.push("accent");
  if (kind === "ghost")   cls.push("ghost");
  if (size === "sm")      cls.push("sm");
  return (
    <button
      type={type} className={cls.join(" ")} onClick={onClick}
      disabled={disabled} style={style} title={title}
    >
      {ico && <Ic name={ico} />}
      {children}
    </button>
  );
}

export function IconBtn({
  ico, onClick, label,
}: { ico: string; onClick?: () => void; label: string }) {
  return (
    <button
      className="btn ghost sm" onClick={onClick} aria-label={label}
      style={{ padding: 6 }}
    >
      <Ic name={ico} size={14} />
    </button>
  );
}

interface AvatarProps {
  id: string | null | undefined;
  size?: "xs" | "sm" | "md";
  onClick?: React.MouseEventHandler;
  title?: string;
}

export function Avatar({ id, size, onClick, title }: AvatarProps) {
  const { data } = useAppData();
  // Resolve by user id, falling back to initials (anti-portfolio rows and
  // other legacy data reference people by initials).
  const member: TeamMember | undefined = findMember(data?.config.team, id);
  const cls = ["av"];
  if (size) cls.push(size);
  if (!member) cls.push("unassigned");
  return (
    <span
      className={cls.join(" ")}
      onClick={onClick}
      title={title || (member ? member.name : "Unassigned")}
      style={{ cursor: onClick ? "pointer" : undefined }}
    >
      {member ? member.initials : ""}
    </span>
  );
}

const PILL_LABELS: Record<Status, string> = {
  new: "New", review: "Reviewed", diligence: "In diligence",
  invested: "Invested", passed: "Passed", rejected: "Rejected",
};

export function Pill({
  kind, children,
}: { kind: Status; children?: React.ReactNode }) {
  return (
    <span className={"pill " + kind}>
      <span className="dot" />
      {children ?? PILL_LABELS[kind] ?? kind}
    </span>
  );
}

interface TagProps {
  children: React.ReactNode;
  count?: number;
  color?: string;
  outlined?: boolean;
  onClick?: React.MouseEventHandler;
}

export function Tag({ children, count, color, outlined, onClick }: TagProps) {
  return (
    <span className={"tag" + (outlined ? " outlined" : "")} onClick={onClick}>
      {color && <span className="swatch" style={{ background: color }} />}
      {children}
      {count != null && <span className="count">{count}</span>}
    </span>
  );
}

interface ChipProps {
  children: React.ReactNode;
  on?: boolean;
  removable?: boolean;
  onClick?: React.MouseEventHandler;
  onRemove?: () => void;
}

export function Chip({ children, on, removable, onClick, onRemove }: ChipProps) {
  return (
    <span className={"chip" + (on ? " on" : "")} onClick={onClick}>
      {children}
      {removable && (
        <span
          className="x"
          onClick={(e) => { e.stopPropagation(); onRemove?.(); }}
        >×</span>
      )}
    </span>
  );
}

export function Score({
  value, size = "md", showBar = true,
}: { value: number | null; size?: "sm" | "md" | "lg" | "xl"; showBar?: boolean }) {
  const sizes: Record<string, number> = { sm: 13, md: 16, lg: 20, xl: 28 };
  if (value == null) return <span className="score num">—</span>;
  return (
    <div className="score-block">
      <span className="score" style={{ fontSize: sizes[size] }}>{value}</span>
      {showBar && (
        <span className="score-bar">
          <i style={{ width: Math.max(2, value) + "%" }} />
        </span>
      )}
    </div>
  );
}

export function ScoreGlyph({ value, bars = 5 }: { value: number | null; bars?: number }) {
  if (value == null) return <span className="score-glyph" />;
  const filled = Math.round((value / 100) * bars);
  return (
    <span className="score-glyph">
      {Array.from({ length: bars }).map((_, i) => (
        <i
          key={i} className={i < filled ? "" : "off"}
          style={{ height: 3 + i * 1.5 }}
        />
      ))}
    </span>
  );
}

export function Toggle({
  on, onChange,
}: { on?: boolean; onChange?: (next: boolean) => void }) {
  return (
    <span
      className={"toggle" + (on ? " on" : "")}
      onClick={() => onChange?.(!on)}
    />
  );
}

export function Tabs({
  tabs, value, onChange,
}: { tabs: string[]; value: string; onChange: (t: string) => void }) {
  return (
    <div className="tabs">
      {tabs.map(t => (
        <span
          key={t}
          className={"tab" + (t === value ? " on" : "")}
          onClick={() => onChange(t)}
        >{t}</span>
      ))}
    </div>
  );
}

interface TextInputProps {
  value?: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  ico?: string;
  kbd?: string;
  search?: boolean;
  autoFocus?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  style?: React.CSSProperties;
}

export function TextInput({
  value, onChange, placeholder, ico, kbd, search, autoFocus, onKeyDown, style,
}: TextInputProps) {
  return (
    <div className={"input" + (search ? " search" : "")} style={style}>
      {ico && <Ic name={ico} />}
      <input
        autoFocus={autoFocus}
        placeholder={placeholder}
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        onKeyDown={onKeyDown}
      />
      {kbd && <span className="kbd">{kbd}</span>}
    </div>
  );
}

export function TextArea({
  value, onChange, placeholder, rows = 3,
}: {
  value?: string; onChange?: (v: string) => void;
  placeholder?: string; rows?: number;
}) {
  return (
    <textarea
      className="input" rows={rows} placeholder={placeholder}
      value={value || ""} onChange={(e) => onChange?.(e.target.value)}
    />
  );
}
